<template>
    <div class="ui-gallery">
        <div v-if="items.length > 0" class="ui-gallery__thumbnails">
            <div
                v-if="items.length > 3"
                class="ui-gallery__more ui-gallery__more_top"
                :class="{ disabled: isFirstSet }"
                @click="prevItems"
            >
                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
            </div>

            <!-- Отображаем только видимые элементы -->
            <div class="ui-gallery__thumbnails-list">
                <div
                    v-for="(item, index) in visibleItems"
                    :key="index"
                    class="ui-gallery__thumbnail"
                    :class="{ 'selected': index + currentIndex === mainImageIndex }"
                >
                    <a
                        :href="item.src"
                        class="ui-gallery__link"
                        @click.prevent="mainImageIndex = index + currentIndex"
                    >
                        <template v-if="isVideo(item)">
                            <div class="ui-gallery__video">
                                <img
                                    :src="item.thumb"
                                    @error="onImageError"
                                />

                                <div class="ui-gallery__play-icon">
                                    <img
                                        v-svg-inline
                                        class="ui-gallery__play-img"
                                        src="@/assets/icons/ui/play.svg"
                                    />
                                </div>
                            </div>
                        </template>
                        <template v-else>
                            <img
                                :src="item.thumb"
                                class="ui-gallery__image"
                                @error="onImageError"
                            />
                        </template>
                    </a>
                </div>
            </div>

            <!-- Нижняя кнопка -->
            <div
                v-if="items.length > 3"
                class="ui-gallery__more ui-gallery__more--bottom"
                :class="{ disabled: isLastSet }"
                @click="nextItems"
            >
                <img v-svg-inline src="@/assets/icons/ui/arrow-temp.svg" />
            </div>
        </div>

        <!-- Главное изображение -->
        <div v-if="items.length > 0" class="ui-gallery__main">
            <a
                :href="items[mainImageIndex]?.src"
                class="ui-gallery__link ui-gallery__main-item"
                @click.prevent="openGallery(mainImageIndex)"
            >
                <template v-if="isVideo(items[mainImageIndex])">
                    <div class="ui-gallery__video">
                        <video :src="items[mainImageIndex].src" controls>
                            Ваш браузер не поддерживает HTML5 видео.
                        </video>
                    </div>
                </template>
                <template v-else-if="items[mainImageIndex]?.src">
                    <img
                        :src="items[mainImageIndex].src"
                        class="ui-gallery__image"
                        @error="onImageError"
                    />
                </template>
            </a>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import noPhotoImage from '@/assets/images/cars/no-photo.webp';

    import 'lightgallery/css/lightgallery.css';
    import 'lightgallery/css/lg-video.css';

    import { PropType, ref, onMounted, computed, watch } from 'vue';
    import lightGallery from 'lightgallery';
    import lgVideo from 'lightgallery/plugins/video';
    import { GalleryItem } from 'lightgallery/lg-utils';

    const props = defineProps({
        items: {
            type: Array as PropType<Array<{ src: string, thumb: string, type?: string, content_type?: string }>>,
            required: true,
        },
    });

    const galleryRef = ref<ReturnType<typeof lightGallery> | null>(null);
    const currentIndex = ref(0);
    const mainImageIndex = ref(0);

    // Вычисляем видимые элементы
    const visibleItems = computed(() => {
        const startIndex = currentIndex.value;
        const endIndex = Math.min(currentIndex.value + 4, props.items.length);

        return props.items.slice(startIndex, endIndex);
    });

    // Определяем, достигнут ли конец списка
    const isLastSet = computed(() =>
        currentIndex.value + 3 >= props.items.length);

    // Определяем, достигнут ли начало списка
    const isFirstSet = computed(() =>
        currentIndex.value === 0);

    watch(
        () => props.items,
        () => initGallery(),
        { deep: true },
    );

    function openGallery(index: number): void {
        if (galleryRef.value) {
            galleryRef.value.openGallery(index);
        }
    }

    // Обработчик события error для <img>
    function onImageError(e: Event): void {
        (e.target as HTMLImageElement).src = noPhotoImage;
    }

    function nextItems(): void {
        if (!isLastSet.value) {
            currentIndex.value = Math.min(currentIndex.value + 1, props.items.length - 3);
        }
    }

    // Переход к предыдущему набору элементов
    function prevItems(): void {
        if (!isFirstSet.value) {
            currentIndex.value = Math.max(currentIndex.value - 1, 0);
        }
    }

    function isVideo(item: { src: string, thumb: string, type?: string }): boolean {
        return item?.type === 'video';
    }

    function initGallery(): void {
        if (galleryRef.value !== null) {
            galleryRef.value.destroy();
        }

        galleryRef.value = lightGallery(
            document.querySelector('.ui-gallery') as HTMLElement,
            {
                plugins: [lgVideo],
                dynamic: true,
                dynamicEl: props.items.map((item) => {
                    if (isVideo(item)) {
                        return {
                            thumb: item.thumb,
                            video: {
                                source: [{
                                    src: item.src,
                                    type: item.content_type,
                                }],
                                attributes: {
                                    controls: true,
                                },
                            },
                        } as GalleryItem;
                    }
                    else {
                        return {
                            thumb: item.thumb,
                            src: item.src,
                        } as GalleryItem;
                    }
                }),
            },
        );
    }

    onMounted(() => {
        initGallery();
    });
</script>

<style lang="scss" scoped>
    @use './UIGalleryWithSlider.scss' as *;
</style>
